// Reader-Progress-Store (2026-09-11) — die EINE Quelle fuer "wie weit hat
// der Leser dieses Scrolly gescrollt" (0..1), gespeist vom Renderer.
//
// Hintergrund: Der Vike-Reader scrollt im Container (ViewerChrome
// useBodyScroll=false). Die alte computeScrollPercent() mass window.scrollY
// gegen document.scrollHeight — bei nicht scrollbarem Dokument lieferte sie
// sofort 1. Prod-Befund WK 11.09.2026: 2518 Sessions maxProgress=1, 425
// maxProgress=0, nur 29 dazwischen. Folge: View-Ping beim Mount (views =
// Seitenaufrufe) und Scroll-Through-Rate ~100 % der gezaehlten Views.
//
// Jetzt: ScrollyViewer.onScrollProgress (scrollTop / (scrollHeight -
// clientHeight) des Renderer-Containers, 1 s gethrottelt) schreibt hierher,
// useViewTracking + useDurationTracking lesen/abonnieren. Keyed per
// publicKey, damit ein Client-Routing zwischen Scrollys keinen alten Stand
// mitnimmt. Reiner Modul-State: kein React, SSR-sicher (nur Maps).

type Listener = (percent: number) => void;

const progressByKey = new Map<string, number>();
const listenersByKey = new Map<string, Set<Listener>>();

function clamp01(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.max(0, Math.min(1, value));
}

/** Renderer meldet den aktuellen Scroll-Stand (0..1) fuer ein Scrolly. */
export function publishReaderProgress(publicKey: string, percent: number): void {
  if (!publicKey) return;
  const next = clamp01(percent);
  progressByKey.set(publicKey, next);
  const listeners = listenersByKey.get(publicKey);
  if (!listeners) return;
  for (const listener of listeners) {
    try {
      listener(next);
    } catch {
      // Ein kaputter Abonnent darf weder Renderer noch andere Abonnenten stoeren.
    }
  }
}

/** Zuletzt gemeldeter Stand; 0 wenn der Renderer noch nichts gemeldet hat. */
export function readReaderProgress(publicKey: string): number {
  return progressByKey.get(publicKey) ?? 0;
}

/**
 * Abonniert Fortschritts-Meldungen fuer ein Scrolly. Liefert die
 * Abmelde-Funktion. Der aktuelle Stand wird NICHT sofort nachgeliefert —
 * dafuer readReaderProgress() beim Setup.
 */
export function subscribeReaderProgress(
  publicKey: string,
  listener: Listener,
): () => void {
  if (!publicKey) return () => undefined;
  let listeners = listenersByKey.get(publicKey);
  if (!listeners) {
    listeners = new Set();
    listenersByKey.set(publicKey, listeners);
  }
  listeners.add(listener);
  return () => {
    listeners?.delete(listener);
    if (listeners && listeners.size === 0) listenersByKey.delete(publicKey);
  };
}

/** Stand verwerfen (Renderer-Unmount) — der naechste Besuch startet bei 0. */
export function clearReaderProgress(publicKey: string): void {
  progressByKey.delete(publicKey);
}
